// useStrategyEntitlements — what strategy CONTENT the effective user is licensed
// to build: the Team Management Alts / FIA switches, enforced.
//
// This is a different question from useFeatureFlags. That hook answers "does
// this product feature exist for me" (feature_flags: global / org pin / user
// pin). This one answers "has my upline licensed Alts / FIA for me" — the
// deepest-explicit-wins cascade over org_feature_flags + user_feature_flags,
// resolved server-side by get_strategy_entitlements() so the wizard can never
// disagree with what the Team Management tree shows for the same agent.
//
// Platform staff (super_admin / platform_admin) always resolve to entitled —
// they hold the switches; the switches apply to the downline.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/safeSupabase';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';

export interface StrategyEntitlements {
  alts: boolean;
  fia: boolean;
}

export const ALL_ENTITLED: StrategyEntitlements = Object.freeze({ alts: true, fia: true });

/**
 * Turn the RPC result into an entitlement set.
 *
 * Fails SOFT: an error (the migration not yet applied, a transport failure)
 * resolves to fully entitled — today's behavior — rather than stripping every
 * advisor of Alts/FIA because one request failed. A well-formed payload is
 * read strictly: only an explicit `false` turns a lever off.
 */
export function resolveEntitlements(data: unknown, error: unknown): StrategyEntitlements {
  if (error || !data || typeof data !== 'object') return ALL_ENTITLED;
  const d = data as Record<string, unknown>;
  return { alts: d.alts !== false, fia: d.fia !== false };
}

type EntitlementsRpc = (
  fn: 'get_strategy_entitlements',
  args: { p_view_as: string | null },
) => Promise<{ data: unknown; error: Error | null }>;

export function useStrategyEntitlements(): { entitlements: StrategyEntitlements; isLoading: boolean } {
  const { user } = useAuth();
  const { isImpersonating, impersonatedUserId } = useImpersonation();
  // Effective user, not session user: under View-as the wizard must show what
  // the impersonated advisor is licensed for, and the server resolves
  // p_view_as only for a super admin.
  const viewAs = (isImpersonating && impersonatedUserId) || null;
  const effectiveUserId = viewAs ?? user?.id;

  const { data, isLoading } = useQuery({
    // CACHE KEY SCOPING — effective user id, so impersonation never bleeds
    // one advisor's licence into another's view. CLAUDE.md rule #9.
    queryKey: ['strategy-entitlements', effectiveUserId],
    queryFn: async () => {
      // `get_strategy_entitlements` isn't in the auto-generated Supabase types
      // yet (Drew regenerates those); cast through unknown like useAuth does
      // for is_platform_staff_org.
      const { data, error } = await (supabase.rpc as unknown as EntitlementsRpc)(
        'get_strategy_entitlements',
        { p_view_as: viewAs },
      );
      return resolveEntitlements(data, error);
    },
    enabled: !!effectiveUserId,
    staleTime: 60_000,
  });

  // While the first request is in flight the card renders as entitled; the
  // lever is withdrawn the moment the answer lands. Optimistic on purpose —
  // an unlicensed advisor gets at most a sub-second window, and a strategy
  // that picked up an Alt in that window shows the "not enabled" notice.
  return { entitlements: data ?? ALL_ENTITLED, isLoading };
}
